/**
 * ProvePage — «Prove» (atlante cap. 489): cosa si può far provare di centralino e chatbot, e i link mandati. Risponde a due
 * domande: «quale prova mando a questa persona?» e «chi l'ha aperta?». Chi manda un link qui non usa mai i numeri delle
 * aziende clienti: risponde l'azienda dimostrativa.
 *
 * Tutto dalle funzioni del server (`services/proveService.ts`).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
  Paper, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';
import { SoftChip } from '../components/SoftChip';
import { DialogoProva } from '../components/prove/DialogoProva';
import { DialogoInvito } from '../components/prove/DialogoInvito';
import { nomeProdotto } from '../components/prove/nomeProdotto';
import { useFormati } from '../components/partner/formati';
import {
  leggiProve, motivoDellErrore, revocaInvito, type DatiProve, type InvitoProva, type Prova,
} from '../services/proveService';

const TitoloSezione: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.1rem', color: tokens.color.graphite }}>{children}</Typography>
);

export const ProvePage: React.FC = () => {
  const { t } = useTranslation('prove');
  const f = useFormati();
  const [dati, setDati] = useState<DatiProve | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [messaggio, setMessaggio] = useState<string | null>(null);
  const [provaAperta, setProvaAperta] = useState<Prova | 'nuova' | null>(null);
  const [perIlLink, setPerIlLink] = useState<Prova | null>(null);
  const [daRevocare, setDaRevocare] = useState<InvitoProva | null>(null);

  const testoErrore = useCallback((e: unknown) => {
    const motivo = motivoDellErrore(e);
    return motivo ? t(`motivi.${motivo}`) : t('messaggi.errore_generico');
  }, [t]);

  const leggi = useCallback(async () => {
    setErrore(null);
    try {
      setDati(await leggiProve());
    } catch (e) {
      setErrore(testoErrore(e));
    }
  }, [testoErrore]);

  useEffect(() => { void leggi(); }, [leggi]);

  const copia = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setMessaggio(t('messaggi.link_copiato'));
    } catch (e) {
      console.error('[prove] copia del link non riuscita', e);
      setMessaggio(t('messaggi.copia_non_riuscita'));
    }
  };

  const revoca = async (invito: InvitoProva) => {
    setDaRevocare(null);
    try {
      await revocaInvito(invito.codice);
      setMessaggio(t('messaggi.link_revocato'));
      await leggi();
    } catch (e) {
      setErrore(testoErrore(e));
    }
  };

  const azienda = dati?.azienda ?? null;
  const etichettaProva = (id: string) => dati?.prove.find((p) => p.id === id)?.etichetta ?? t('inviti.prova_eliminata');

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: { xs: '1.6rem', md: '1.95rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', color: tokens.color.graphite, fontVariationSettings: '"WONK" 0, "SOFT" 30' }}>
            {t('titolo')}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 640 }}>{t('sottotitolo')}</Typography>
        </Box>
        <Tooltip title={t('aggiorna')}>
          <IconButton onClick={() => void leggi()} aria-label={t('aggiorna')}><Refresh /></IconButton>
        </Tooltip>
      </Stack>

      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

      {dati === null && !errore && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>}

      {dati && (
        <Stack spacing={4}>
          {!azienda && <Alert severity="info">{t('azienda.non_impostata')}</Alert>}
          {azienda && !azienda.pronta && <Alert severity="warning">{t('azienda.non_pronta', { motivo: t(`motivi.${azienda.mancano[0]}`) })}</Alert>}
          {azienda?.pronta && azienda.numero && <Typography variant="body2" sx={{ color: tokens.color.ash }}>{t('azienda.pronta', { nome: azienda.nome, numero: azienda.numero })}</Typography>}

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <TitoloSezione>{t('elenco.titolo')}</TitoloSezione>
              {azienda && <Button variant="outlined" size="small" onClick={() => setProvaAperta('nuova')} sx={{ textTransform: 'none', minHeight: 36 }}>{t('elenco.nuova')}</Button>}
            </Stack>
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              {dati.prove.length === 0 ? (
                <Typography sx={{ p: 4, color: tokens.color.ash }}>{t('elenco.vuoto')}</Typography>
              ) : (
                <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('elenco.prova')}</TableCell>
                      <TableCell>{t('elenco.prodotto')}</TableCell>
                      <TableCell>{t('elenco.stato')}</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dati.prove.map((p) => (
                      <TableRow key={p.id} sx={{ verticalAlign: 'top' }}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600 }}>{p.etichetta}</Typography>
                          {p.descrizione && <Typography variant="caption" sx={{ color: tokens.color.ash }}>{p.descrizione}</Typography>}
                        </TableCell>
                        <TableCell>{p.categoria ? nomeProdotto(p.categoria) : t('elenco.lo_chiede_l_assistente')}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          {p.pronta
                            ? <SoftChip label={t('elenco.pronta')} tone="success" />
                            : p.motivo && <Typography variant="body2" sx={{ color: tokens.color.warning }}>{t(`motivi.${p.motivo}`)}</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {azienda && <Button size="small" onClick={() => setProvaAperta(p)} sx={{ textTransform: 'none', minHeight: 36 }}>{t('elenco.modifica')}</Button>}
                          <Button size="small" variant="outlined" disabled={!p.pronta} onClick={() => setPerIlLink(p)} sx={{ textTransform: 'none', minHeight: 36, ml: 1 }}>{t('elenco.crea_link')}</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>

          <Box>
            <Box sx={{ mb: 1 }}><TitoloSezione>{t('inviti.titolo')}</TitoloSezione></Box>
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              {dati.inviti.length === 0 ? (
                <Typography sx={{ p: 4, color: tokens.color.ash }}>{t('inviti.vuoto')}</Typography>
              ) : (
                <Table size="small" sx={{ minWidth: 820 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('inviti.per_chi')}</TableCell>
                      <TableCell>{t('inviti.prova')}</TableCell>
                      <TableCell>{t('inviti.usato')}</TableCell>
                      <TableCell>{t('inviti.scade')}</TableCell>
                      <TableCell>{t('inviti.stato')}</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dati.inviti.map((i) => (
                      <TableRow key={i.codice}>
                        <TableCell>{i.nome ?? t('inviti.senza_nome')}</TableCell>
                        <TableCell>{etichettaProva(i.prova_id)}</TableCell>
                        <TableCell>{i.usi_massimi === null ? t('inviti.usi_pubblico', { usi: i.usi }) : t('inviti.usi', { usi: i.usi, massimo: i.usi_massimi })}</TableCell>
                        <TableCell>{i.scade_il ? f.giorno(i.scade_il.slice(0, 10)) : ''}</TableCell>
                        <TableCell>
                          <SoftChip label={t(`inviti.stati.${i.stato}`)} tone={i.stato === 'attivo' ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {i.stato === 'attivo' && i.link && (
                            <>
                              <Button size="small" onClick={() => setDaRevocare(i)} sx={{ textTransform: 'none', minHeight: 36 }}>{t('inviti.revoca')}</Button>
                              <Button size="small" variant="outlined" onClick={() => void copia(i.link as string)} sx={{ textTransform: 'none', minHeight: 36, ml: 1 }}>{t('inviti.copia')}</Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>
        </Stack>
      )}

      {provaAperta && azienda && dati && (
        <DialogoProva
          prova={provaAperta === 'nuova' ? null : provaAperta}
          azienda={azienda}
          missioni={dati.missioni}
          onChiudi={() => setProvaAperta(null)}
          onSalvata={() => {
            setProvaAperta(null);
            setMessaggio(t('messaggi.prova_salvata'));
            void leggi();
          }}
        />
      )}

      {perIlLink && (
        <DialogoInvito
          prova={perIlLink}
          onChiudi={() => setPerIlLink(null)}
          onCreato={() => { void leggi(); }}
          onCopia={(link) => { void copia(link); }}
        />
      )}

      <Dialog open={!!daRevocare} onClose={() => setDaRevocare(null)} maxWidth="xs">
        <DialogTitle>{t('dialogo_revoca.titolo')}</DialogTitle>
        <DialogContent><DialogContentText>{t('dialogo_revoca.testo')}</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setDaRevocare(null)} sx={{ textTransform: 'none', minHeight: 40 }}>{t('dialogo_revoca.annulla')}</Button>
          <Button color="error" variant="contained" onClick={() => { if (daRevocare) void revoca(daRevocare); }} sx={{ textTransform: 'none', minHeight: 40 }}>{t('dialogo_revoca.conferma')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!messaggio} autoHideDuration={4000} onClose={() => setMessaggio(null)} message={messaggio} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};
